import React from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "utils/queryClient";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import LoginPage from "./pages/login-page/LoginPage";
import ForgotPasswordPage from "pages/login-page/forgot-password/ForgotPasswordPage";
import MerchantPage from "./pages/merchant-page/MerchantPage";
import ProtectedRoute from "components/protected-route/ProtectedRoute";
import {
  MERCHANT,
  MERCHANT_NEEDS_PASSWORD_CHANGE,
} from "components/protected-route/access";
import GuestOnlyRoute from "components/guest-only-route/GuestOnlyRoute";
import MerchantLayout from "mianLayouts/MerchantLayout";
import NotFoundPage from "./pages/not-found-page/NotFoundPage";
import "./index.css";
import ChangePasswordPage from "pages/login-page/change-password/ChangePasswordPage";
import UpdatePasswordPage from "pages/update-password/UpdatePasswordPage";

function AppV1() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          <Route
            path="/"
            element={
              <GuestOnlyRoute
                allowedUserTypes={[MERCHANT]}
                redirectTo="/merchant"
              >
                <LoginPage />
              </GuestOnlyRoute>
            }
          />
          {/* Signed-out recovery. Guarded like the login page it sits beside: a signed-in
              merchant is bounced to /merchant, since they want /update-password instead. Firebase
              hosts the page the emailed link opens, so there is no /reset-password route here. */}
          <Route
            path="/forgot-password"
            element={
              <GuestOnlyRoute
                allowedUserTypes={[MERCHANT]}
                redirectTo="/merchant"
              >
                <ForgotPasswordPage />
              </GuestOnlyRoute>
            }
          />

          {/* Protected, not guest-only: the forced-rotation flow arrives here
              already signed in. useLoginForm dispatches setUserType right after
              the credential check — BEFORE the mustChangePassword branch — which
              is what lets this route be guarded at all. Moving that dispatch
              back after the branch would lock merchants out of the one page that
              clears mustChangePassword.
              The guard must wrap the ELEMENT, never the <Route>: React Router
              throws "is not a <Route> component" for any non-Route child of
              <Routes>, in production as well as dev. */}
          <Route
            path="/change-password"
            element={
              <ProtectedRoute
                allowedUserTypes={[MERCHANT_NEEDS_PASSWORD_CHANGE]}
              >
                <ChangePasswordPage />
              </ProtectedRoute>
            }
          />
          {/* Voluntary change, reached from the settings menu while signed in. Deliberately its
              OWN route rather than widening /change-password's guard: that page serves the forced
              rotation, never asks for a current password, and its guard is load-bearing (see
              access.ts). Both endings here sign out, so there is nothing to navigate back to. */}
          <Route
            path="/update-password"
            element={
              <ProtectedRoute allowedUserTypes={[MERCHANT]}>
                <UpdatePasswordPage />
              </ProtectedRoute>
            }
          />

          {/* ProtectedRoute wraps the LAYOUT, not the page. Two reasons: an
              unauthorized visitor should not see merchant chrome (nav, network
              selector, settings) at all, and the session-changed screen it can
              render is a full-page shell — nested inside gp-layout-inner's
              1280px container it painted its own background as a visible
              rectangle. */}
          <Route
            path="/merchant"
            element={
              <ProtectedRoute allowedUserTypes={[MERCHANT]}>
                <MerchantLayout>
                  <MerchantPage />
                </MerchantLayout>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}

export default AppV1;
